import { LitElement, html, type CSSResultGroup } from 'lit';
import { property, state, query } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { emit } from '../utils/dom.utils.js';
import styles from './styles.js';
export interface EditorChangeEventDetail {
  html: string;
  text: string;
}

interface FormatState {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strikethrough: boolean;
  ul: boolean;
  blockquote: boolean;
  h1: boolean;
  h2: boolean;
  h3: boolean;
  link: boolean;
  alignLeft: boolean;
  alignCenter: boolean;
  alignRight: boolean;
}

const EMPTY_FMT: FormatState = {
  bold: false, italic: false, underline: false, strikethrough: false,
  ul: false, blockquote: false, h1: false, h2: false, h3: false,
  link: false, alignLeft: true, alignCenter: false, alignRight: false,
};

/**
 * uwc-editor
 *
 * A lightweight rich-text editor built on the native contenteditable API and
 * document.execCommand(). Zero external dependencies — works correctly inside
 * Shadow DOM unlike Quill / Squire which rely on APIs that do not pierce shadow
 * boundaries.
 *
 * @attr {string}  placeholder - Placeholder shown when the editor is empty.
 * @attr {boolean} readonly    - Hides the toolbar; content is non-editable.
 * @attr {boolean} disabled    - Disables all interaction.
 * @attr {boolean} invalid     - Applies error border styling.
 * @attr {boolean} fluid       - Stretches to container width.
 * @attr {string}  label       - aria-label for the content region.
 *
 * @prop {string}  value       - Current HTML content.
 *
 * @fires uwc-change  — On every user edit. Detail: `{ html, text }`.
 * @fires uwc-focus   — Editor gained focus.
 * @fires uwc-blur    — Editor lost focus.
 *
 * @csspart base    - Outermost wrapper div.
 * @csspart toolbar - Toolbar row.
 * @csspart content - Contenteditable editing area.
 */
export class UwcEditor extends LitElement {
  static override styles: CSSResultGroup = [styles];

  @query('.uwc-editor__content') private _contentEl!: HTMLDivElement;

  @property({ attribute: false }) value = '';
  @property() placeholder = 'Write something…';
  @property({ type: Boolean, reflect: true }) readonly = false;
  @property({ type: Boolean, reflect: true }) disabled = false;
  @property({ type: Boolean, reflect: true }) invalid = false;
  @property({ type: Boolean, reflect: true }) fluid = false;
  @property() label = 'Rich text editor';

  @state() private _fmt: FormatState = { ...EMPTY_FMT };
  @state() private _showLinkBar = false;
  @state() private _showImageBar = false;
  @state() private _linkUrl = '';
  @state() private _imageUrl = '';
  @state() private _imageAlt = '';

  private _internalUpdate = false;
  private _isFocused = false;
  private _savedRange: Range | null = null;

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  override firstUpdated(): void {
    document.addEventListener('selectionchange', this._onSelectionChange);
    if (this.value && this._contentEl) {
      this._contentEl.innerHTML = this.value;
    }
    this._contentEl.dataset.placeholder = this.placeholder;
    this._contentEl.setAttribute('aria-label', this.label);
  }

  override updated(changed: Map<string, unknown>): void {
    if (changed.has('value') && !this._internalUpdate && this._contentEl) {
      this._contentEl.innerHTML = this.value || '';
    }
    if (changed.has('placeholder') && this._contentEl) {
      this._contentEl.dataset.placeholder = this.placeholder;
    }
    this._internalUpdate = false;
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    document.removeEventListener('selectionchange', this._onSelectionChange);
  }

  // ── Selection & format state ───────────────────────────────────────────────

  private _onSelectionChange = (): void => {
    if (!this._isFocused) return;
    this._updateFormatState();
  };

  private _updateFormatState(): void {
    try {
      this._fmt = {
        bold:          document.queryCommandState('bold'),
        italic:        document.queryCommandState('italic'),
        underline:     document.queryCommandState('underline'),
        strikethrough: document.queryCommandState('strikeThrough'),
        ul:            document.queryCommandState('insertUnorderedList'),
        blockquote:    this._isInBlock('BLOCKQUOTE'),
        h1:            this._isInBlock('H1'),
        h2:            this._isInBlock('H2'),
        h3:            this._isInBlock('H3'),
        link:          this._isInBlock('A'),
        alignLeft:     document.queryCommandState('justifyLeft'),
        alignCenter:   document.queryCommandState('justifyCenter'),
        alignRight:    document.queryCommandState('justifyRight'),
      };
    } catch { /* ignore */ }
  }

  /** Traverse from the selection anchor up to the editing root looking for tagName. */
  private _isInBlock(tagName: string): boolean {
    const sel = document.getSelection();
    if (!sel || sel.rangeCount === 0) return false;
    let node: Node | null = sel.getRangeAt(0).commonAncestorContainer;
    while (node && node !== this._contentEl) {
      if ((node as Element).tagName === tagName) return true;
      node = node.parentNode;
    }
    return false;
  }

  // ── Command execution ──────────────────────────────────────────────────────

  /**
   * Execute a document.execCommand() after focusing the editing area.
   * Prevents toolbar button clicks from ever stealing the caret.
   *
   * KNOWN DEPRECATION: `document.execCommand` is a deprecated API (flagged by
   * Chrome's modern-web-guidance). It remains the de-facto rich-text editing
   * mechanism — there is no fully standardized replacement yet. Migrating to a
   * Selection/Range-based model is a large, separate effort tracked as a
   * follow-up; do NOT bundle that rewrite into routine changes here.
   */
  private _exec(cmd: string, value?: string): void {
    this._contentEl?.focus();
    document.execCommand(cmd, false, value ?? '');
    this._updateFormatState();
    this._emitChange();
  }

  private _toggleHeading(): void {
    const { h1, h2, h3 } = this._fmt;
    if (h1)         this._exec('formatBlock', 'h2');
    else if (h2)    this._exec('formatBlock', 'h3');
    else if (h3)    this._exec('formatBlock', 'p');
    else            this._exec('formatBlock', 'h1');
  }

  // ── Link helpers ───────────────────────────────────────────────────────────

  private _saveRange(): void {
    const sel = document.getSelection();
    if (sel && sel.rangeCount > 0) {
      this._savedRange = sel.getRangeAt(0).cloneRange();
    }
  }

  private _restoreRange(): void {
    if (!this._savedRange) return;
    this._contentEl.focus();
    const sel = document.getSelection();
    if (sel) {
      sel.removeAllRanges();
      sel.addRange(this._savedRange);
    }
  }

  private _applyLink(): void {
    if (!this._linkUrl.trim()) return;
    const url = /^https?:\/\//i.test(this._linkUrl)
      ? this._linkUrl
      : 'https://' + this._linkUrl;
    this._restoreRange();
    document.execCommand('createLink', false, url);
    // Ensure links open in a new tab
    this._contentEl.querySelectorAll('a:not([target])').forEach(a => {
      (a as HTMLAnchorElement).target = '_blank';
      (a as HTMLAnchorElement).rel = 'noopener noreferrer';
    });
    this._linkUrl = '';
    this._showLinkBar = false;
    this._updateFormatState();
    this._emitChange();
  }

  private _removeLink(): void {
    this._restoreRange();
    document.execCommand('unlink');
    this._showLinkBar = false;
    this._updateFormatState();
    this._emitChange();
  }

  // ── Image helper ───────────────────────────────────────────────────────────

  private _insertImage(): void {
    if (!this._imageUrl.trim()) return;
    const alt = this._escapeAttr(this._imageAlt || '');
    const src = this._escapeAttr(this._imageUrl);
    this._restoreRange();
    document.execCommand('insertHTML', false, `<img src="${src}" alt="${alt}">`);
    this._imageUrl = '';
    this._imageAlt = '';
    this._showImageBar = false;
    this._emitChange();
  }

  private _escapeAttr(s: string): string {
    return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  // ── Change emission ────────────────────────────────────────────────────────

  private _onInput(): void {
    this._internalUpdate = true;
    this._emitChange();
  }

  private _emitChange(): void {
    if (!this._contentEl) return;
    this.value = this._contentEl.innerHTML;
    emit(this, 'uwc-change', {
      html: this.value,
      text: this._contentEl.textContent ?? '',
    } satisfies EditorChangeEventDetail);
  }

  // ── Toolbar button factory ─────────────────────────────────────────────────

  private _btn(
    label: string,
    icon: string,
    active: boolean,
    title: string,
    onMouseDown: (e: MouseEvent) => void,
    extraClass = '',
  ) {
    return html`
      <uwc-button 
        icon=${icon} 
        icon-only 
        aria-label=${label} 
        text
        title=${title}
        variant="secondary"
        aria-pressed=${String(active)}
        @mousedown=${onMouseDown}
      ></uwc-button>
    `;
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  override render() {
    const f = this._fmt;
    const isHeading = f.h1 || f.h2 || f.h3;
    const headingTitle = f.h1 ? 'Heading (H1 → H2)' : f.h2 ? 'Heading (H2 → H3)' : f.h3 ? 'Heading (H3 → Normal)' : 'Heading (Normal → H1)';

    const cmd = (command: string, value?: string) =>
      (e: MouseEvent) => { e.preventDefault(); this._exec(command, value); };

    return html`
      <div
        class=${classMap({
          'uwc-editor':           true,
          'uwc-editor--readonly': this.readonly,
          'uwc-editor--disabled': this.disabled,
          'uwc-editor--invalid':  this.invalid,
        })}
        part="base"
      >
        ${!this.readonly && !this.disabled ? html`

          <!-- ── Toolbar ── -->
          <div class="uwc-editor__toolbar" part="toolbar" role="toolbar" aria-label="Text formatting">

            <!-- Group 1: Inline formats -->
            <div class="uwc-editor__toolbar-group">
              ${this._btn('', 'type-bold', f.bold,          'Bold (Ctrl+B)',      cmd('bold'),         'uwc-editor__btn--bold')}
              ${this._btn('', 'type-italic', f.italic,        'Italic (Ctrl+I)',    cmd('italic'),       'uwc-editor__btn--italic')}
              ${this._btn('', 'type-underline', f.underline,     'Underline (Ctrl+U)', cmd('underline'),    'uwc-editor__btn--underline')}
              ${this._btn('', 'type-strikethrough', f.strikethrough, 'Strikethrough',      cmd('strikeThrough'),'uwc-editor__btn--strike')}
            </div>

            <span class="uwc-editor__sep" aria-hidden="true"></span>

            <!-- Group 2: Insert -->
            <div class="uwc-editor__toolbar-group">
              ${this._btn('', 'link-45deg',      f.link,       'Insert / edit link', (e) => {
                e.preventDefault();
                this._saveRange();
                this._showImageBar = false;
                if (this._showLinkBar) {
                  this._showLinkBar = false;
                } else {
                  this._linkUrl = '';
                  // Pre-fill if cursor is already inside a link
                  if (f.link) {
                    const sel = document.getSelection();
                    if (sel && sel.rangeCount > 0) {
                      let node: Node | null = sel.getRangeAt(0).commonAncestorContainer;
                      while (node && node !== this._contentEl) {
                        if ((node as Element).tagName === 'A') {
                          this._linkUrl = (node as HTMLAnchorElement).href;
                          break;
                        }
                        node = node.parentNode;
                      }
                    }
                  }
                  this._showLinkBar = true;
                }
              })}
              ${this._btn('', 'list-ul',    f.ul,    'Bullet list',  cmd('insertUnorderedList'))}
              ${this._btn('', 'image',      false,   'Insert image', (e) => {
                e.preventDefault();
                this._saveRange();
                this._showLinkBar = false;
                this._showImageBar = !this._showImageBar;
                if (this._showImageBar) { this._imageUrl = ''; this._imageAlt = ''; }
              })}
              ${this._btn('', 'blockquote-left', f.blockquote, 'Blockquote', cmd('formatBlock', f.blockquote ? 'p' : 'blockquote'))}
            </div>

            <span class="uwc-editor__sep" aria-hidden="true"></span>

            <!-- Group 3: Block / Alignment -->
            <div class="uwc-editor__toolbar-group">
              ${this._btn('H', '', isHeading, headingTitle, (e) => { e.preventDefault(); this._toggleHeading(); }, 'uwc-editor__btn--bold')}
              ${this._btn('', 'text-left',   f.alignLeft,   'Align left',   cmd('justifyLeft'))}
              ${this._btn('', 'text-center', f.alignCenter, 'Align center', cmd('justifyCenter'))}
              ${this._btn('', 'text-right',  f.alignRight,  'Align right',  cmd('justifyRight'))}
            </div>

            <span class="uwc-editor__sep" aria-hidden="true"></span>

            <!-- Group 4: History -->
            <div class="uwc-editor__toolbar-group">
              ${this._btn('', 'arrow-counterclockwise', false, 'Undo (Ctrl+Z)',      (e) => { e.preventDefault(); this._exec('undo'); })}
              ${this._btn('', 'arrow-clockwise',        false, 'Redo (Ctrl+Shift+Z)',(e) => { e.preventDefault(); this._exec('redo'); })}
            </div>

          </div>

          <!-- ── Link bar ── -->
          ${this._showLinkBar ? html`
            <div class="uwc-editor__aux-bar" role="group" aria-label="Insert link">
              <uwc-icon name="link-45deg" size="1em" style="color: var(--uwc-editor-toolbar-color, #64748b); flex-shrink:0;"></uwc-icon>
              <input
                class="uwc-editor__aux-input"
                type="url"
                placeholder="https://example.com"
                .value=${this._linkUrl}
                @input=${(e: InputEvent) => { this._linkUrl = (e.target as HTMLInputElement).value; }}
                @keydown=${(e: KeyboardEvent) => {
                  if (e.key === 'Enter')  { e.preventDefault(); this._applyLink(); }
                  if (e.key === 'Escape') this._showLinkBar = false;
                }}
              >
              <button class="uwc-editor__aux-action" type="button" @click=${this._applyLink}>Apply</button>
              <button class="uwc-editor__aux-action" type="button" @click=${this._removeLink}>Remove</button>
              <button class="uwc-editor__aux-close" type="button" aria-label="Close" @click=${() => { this._showLinkBar = false; }}>
                <uwc-icon name="x" size="0.85em"></uwc-icon>
              </button>
            </div>
          ` : ''}

          <!-- ── Image bar ── -->
          ${this._showImageBar ? html`
            <div class="uwc-editor__aux-bar" role="group" aria-label="Insert image">
              <uwc-icon name="image" size="1em" style="color: var(--uwc-editor-toolbar-color, #64748b); flex-shrink:0;"></uwc-icon>
              <input
                class="uwc-editor__aux-input"
                type="url"
                placeholder="Image URL"
                .value=${this._imageUrl}
                @input=${(e: InputEvent) => { this._imageUrl = (e.target as HTMLInputElement).value; }}
                @keydown=${(e: KeyboardEvent) => {
                  if (e.key === 'Enter')  { e.preventDefault(); this._insertImage(); }
                  if (e.key === 'Escape') this._showImageBar = false;
                }}
              >
              <input
                class="uwc-editor__aux-input uwc-editor__aux-input--alt"
                type="text"
                placeholder="Alt text"
                .value=${this._imageAlt}
                @input=${(e: InputEvent) => { this._imageAlt = (e.target as HTMLInputElement).value; }}
                @keydown=${(e: KeyboardEvent) => {
                  if (e.key === 'Enter')  { e.preventDefault(); this._insertImage(); }
                  if (e.key === 'Escape') this._showImageBar = false;
                }}
              >
              <button class="uwc-editor__aux-action" type="button" @click=${this._insertImage}>Insert</button>
              <button class="uwc-editor__aux-close" type="button" aria-label="Close" @click=${() => { this._showImageBar = false; }}>
                <uwc-icon name="x" size="0.85em"></uwc-icon>
              </button>
            </div>
          ` : ''}

        ` : ''}

        <!-- ── Content editable area ── -->
        <div
          class="uwc-editor__content"
          part="content"
          role="textbox"
          aria-multiline="true"
          aria-label=${this.label}
          contenteditable=${(this.readonly || this.disabled) ? 'false' : 'true'}
          data-placeholder=${this.placeholder}
          spellcheck="true"
          @input=${this._onInput}
          @focus=${() => { this._isFocused = true; this._updateFormatState(); emit(this, 'uwc-focus', {}); }}
          @blur=${() => { this._isFocused = false; emit(this, 'uwc-blur', {}); }}
          @keydown=${this._onKeydown}
        ></div>
      </div>
    `;
  }

  // ── Keyboard shortcuts ─────────────────────────────────────────────────────

  private _onKeydown = (e: KeyboardEvent): void => {
    if (!e.ctrlKey && !e.metaKey) return;
    switch (e.key.toLowerCase()) {
      case 'b': e.preventDefault(); this._exec('bold'); break;
      case 'i': e.preventDefault(); this._exec('italic'); break;
      case 'u': e.preventDefault(); this._exec('underline'); break;
      case 'z':
        e.preventDefault();
        this._exec(e.shiftKey ? 'redo' : 'undo');
        break;
      case 'y': e.preventDefault(); this._exec('redo'); break;
    }
  };

  // ── Public API ─────────────────────────────────────────────────────────────

  override focus(): void {
    this._contentEl?.focus();
  }

  /** Return the editor's plain-text content. */
  getText(): string {
    return this._contentEl?.textContent ?? '';
  }

  /** Programmatically set HTML content. */
  setHTML(html: string): void {
    this.value = html;
    if (this._contentEl) {
      this._contentEl.innerHTML = html;
    }
  }

  /** Clear the editor. */
  clear(): void {
    if (!this._contentEl) return;
    this._contentEl.innerHTML = '';
    this.value = '';
    emit(this, 'uwc-change', { html: '', text: '' } satisfies EditorChangeEventDetail);
  }
}

declare global {
  interface HTMLElementTagNameMap { 'uwc-editor': UwcEditor; }
}
